import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldQuestion } from 'lucide-react';
import { API, Section, Pill, fmt } from './ui';

const Row = ({ k, v }) => <div className="grid gap-1 py-2 sm:grid-cols-[220px_1fr]"><dt className="text-xs font-bold text-slate-500">{k}</dt><dd className="text-sm text-slate-800">{v}</dd></div>;

// Honest, live evidence of where data lives and who can see it. Never shows a rating without an independent assessment.
export const EvidenceTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/api/security/evidence`).then((r) => setD(r.data)).catch(() => toast.error('Could not load evidence')); }, []);
  if (!d) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="evidence-tab">
      <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="evidence-rating">
        <ShieldQuestion className="h-6 w-6 shrink-0 text-slate-500" />
        <div><div className="text-sm font-bold text-slate-800">Security rating: not shown</div><p className="text-sm text-slate-600">{d.rating.statement}</p><p className="mt-1 text-[11px] text-slate-400">Generated {fmt(d.generated_at)}</p></div>
      </div>
      <Section title={`Open risks (${d.open_risks.length})`} testId="evidence-risks">
        <ul className="space-y-1.5 text-sm">{d.open_risks.map((r) => <li key={r} className="flex gap-2 text-slate-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />{r}</li>)}</ul>
      </Section>
      <Section title="Where client data is stored and protected" testId="evidence-storage">
        <dl className="divide-y divide-slate-100">
          <Row k="Storage" v={d.storage.where} />
          <Row k="Encryption at rest" v={<>{d.storage.algorithm} <Pill tone={d.storage.documents_encrypted ? 'info' : 'critical'}>{d.storage.documents_encrypted ? 'active' : 'not configured'}</Pill></>} />
          <Row k="In transit" v={d.storage.in_transit} />
          <Row k="Backups" v={`Last success ${fmt(d.storage.backups.last_success)} · ${d.storage.backups.encrypted ? 'encrypted AES-256-GCM' : 'encryption unknown'}`} />
          <Row k="Access control" v={`${d.access.rbac}. MFA: ${d.access.mfa}. Sessions: ${d.access.sessions}.`} />
          <Row k="Client vs staff" v={d.access.client_vs_staff} />
        </dl>
      </Section>
      <Section title="External AI" testId="evidence-ai">
        <dl className="divide-y divide-slate-100">
          <Row k="Client data sent to AI models?" v={d.ai.external_models_used ? `Yes — ${d.ai.approved_tools.map((t) => `${t.label} (${t.mode})`).join(', ')}` : 'No. Every AI tool is blocked.'} />
          <Row k="Default" v={d.ai.default} />
          <Row k="Model training" v={d.ai.training} />
        </dl>
      </Section>
      <Section title="Providers that can access or process data" testId="evidence-providers">
        <table className="w-full text-left text-xs"><thead className="text-slate-500"><tr><th className="py-1.5">Provider</th><th>Purpose</th><th>Data</th><th>Region</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{d.providers.map((p) => <tr key={p.name}><td className="py-2 font-semibold text-slate-700">{p.name}</td><td>{p.role}</td><td>{p.data}</td><td>{p.region}</td></tr>)}</tbody></table>
      </Section>
      <Section title="Testing and incidents" testId="evidence-testing">
        <dl className="divide-y divide-slate-100">
          <Row k="Independent penetration test" v={d.testing.independent_pentest ? 'Recorded' : 'Not yet — required before claiming an A+ standard'} />
          <Row k="Internal review" v={d.testing.internal_code_review} />
          <Row k="Open alerts" v={`${d.incidents.open_alerts} open (${d.incidents.critical_open} critical)`} />
          <Row k="Breach process" v={d.incidents.process} />
        </dl>
      </Section>
    </div>
  );
};
